package com.steamstreet.vegasful.web.analytics

/**
 * Where the visitor's opt-out choice is kept, in `localStorage`.
 *
 * The same key the app uses for the same choice, so the two surfaces read the same way even
 * though nothing is shared between their stores. Read at the facade boundary on every call, and
 * again in the inline snippet in the page head, so neither a new call site nor GA4's own
 * automatic collection can bypass it.
 */
const val ANALYTICS_OPT_OUT = "vegasful-analytics-opt-out"

/**
 * The analytics event taxonomy for the web.
 *
 * `docs/retention.md` (monorepo root) owns these names and declares them stable once shipped —
 * its launch gates are written in terms of them, so they are not ours to rename. Names are
 * `snake_case` with a past-tense verb, must start with a letter, stay under 40 characters and
 * avoid the `firebase_`, `google_` and `ga_` prefixes; GA4 drops anything else without complaint.
 * Anything added here has to be folded back into that table.
 *
 * **This enum is a hand-maintained copy of the app's
 * `com.steamstreet.vegasful.app.utils.AnalyticsEvent`** (in the separate `vegasful-app`
 * repository). Web and app cannot share a module, so the compiler cannot enforce that the two
 * agree; a diff of the two files is the check. The names must match exactly or a journey that
 * crosses surfaces will not unify in GA4. Entries the web never fires are kept anyway, precisely
 * so that the diff stays clean — see [webEmits] for what this surface actually sends.
 *
 * Three classes of name, kept distinct:
 *
 *  - **GA4-automatic** (`page_view`, `session_start`, `first_visit`, `first_open`, `app_open`,
 *    `user_engagement`) — collected by gtag. Never appear here: emitting them manually either
 *    double-counts or is dropped outright.
 *  - **GA4-recommended** — [SCREEN_VIEW] only, and the app is its only caller. The web has real
 *    documents and gets automatic page collection, so it reports [PAGE_VIEWED] instead.
 *  - **Vegasful-defined** — everything else. Must never collide with a GA4 automatic name.
 */
enum class AnalyticsEvent(val eventName: String) {
    /** GA4-recommended. App-only: single-Activity Compose defeats automatic screen tracking. */
    SCREEN_VIEW("screen_view"),

    /** App-only. The web has no first-launch guide. */
    ONBOARDING_STARTED("onboarding_started"),

    /** App-only. */
    ONBOARDING_COMPLETED("onboarding_completed"),

    EVENT_VIEWED("event_viewed"),
    EVENT_SAVED("event_saved"),
    ENTITY_FOLLOWED("entity_followed"),
    ENTITY_UNFOLLOWED("entity_unfollowed"),
    SEARCH_PERFORMED("search_performed"),
    TICKET_LINK_TAPPED("ticket_link_tapped"),
    SHARE_COMPLETED("share_completed"),

    /** App-only. The web cannot ask for notification permission. */
    PUSH_PROMPT_SHOWN("push_prompt_shown"),

    /** App-only. */
    PUSH_PERMISSION_GRANTED("push_permission_granted"),

    /** App-only. */
    PUSH_PERMISSION_DENIED("push_permission_denied"),

    /** App-only. A push tap opens the app, never the site. */
    PUSH_OPENED("push_opened"),

    /** No weekend guide view exists on either surface yet. */
    DIGEST_ITEM_TAPPED("digest_item_tapped"),

    /** App-only. No per-category preferences screen exists on the web. */
    NOTIFICATION_PREFS_CHANGED("notification_prefs_changed"),

    // ---------------------------------------------------------------------------------------
    // Additions already carried by the app's copy. Not yet in `docs/retention.md`'s table —
    // they follow its conventions and are proposed as additions to it.
    // ---------------------------------------------------------------------------------------

    /** App-only: the first-launch funnel. */
    ONBOARDING_STEP_VIEWED("onboarding_step_viewed"),

    /** App-only. */
    ONBOARDING_STEP_COMPLETED("onboarding_step_completed"),

    /** App-only. */
    ONBOARDING_PREFS_SUBMITTED("onboarding_prefs_submitted"),

    /** App-only. */
    ONBOARDING_DEFERRED("onboarding_deferred"),

    /** A venue, tag, performer or thing detail page. [EVENT_VIEWED] covers events only. */
    ENTITY_VIEWED("entity_viewed"),

    /** The existing `MORE_INFO` interaction, which the taxonomy has no analytics event for. */
    ENTITY_DEPTH_REACHED("entity_depth_reached"),

    /** The inverse of `event_saved`, for the same reason `entity_unfollowed` exists. */
    EVENT_UNSAVED("event_unsaved"),

    SIGNED_IN("signed_in"),
    SIGNED_OUT("signed_out"),

    /** Not emitted on the web: nothing here can tell a first sign-in from a returning one. */
    ACCOUNT_CREATED("account_created"),
    ACCOUNT_DELETED("account_deleted"),

    /**
     * App-only today. The web equivalent — an arrival carrying `utm_*` / `vf_*` — belongs to
     * inbound capture, which is a later phase and is server-side.
     */
    DEEP_LINK_OPENED("deep_link_opened"),

    // =======================================================================================
    // Web additions. `docs/analytics-plan.md` §2.3 proposes these as additions to
    // `docs/retention.md`'s table; they are not in it yet. Everything the web needs and the app
    // does not is in this one block so that folding them back into the spec is a matter of
    // reading one list. Keep new web names here rather than scattered above.
    // =======================================================================================

    /**
     * A page of the site was rendered. Distinct from GA4's automatic `page_view`, which gtag
     * sends on its own and which carries neither the page's kind nor whether the visitor is
     * signed in. Both exist; never sum them.
     */
    PAGE_VIEWED("page_viewed"),

    /** A suggestion in the typeahead dropdown, or a card on the results page, was followed. */
    SEARCH_RESULT_TAPPED("search_result_tapped"),

    /** A tag filter on a listing changed, including being cleared. */
    FILTER_APPLIED("filter_applied"),

    /** A date range was chosen from the calendar control. */
    DATE_SELECTED("date_selected"),

    /** The venue map was used: a point, a list row, or a breadcrumb. */
    MAP_INTERACTED("map_interacted"),

    /** The app-install interstitial was put on screen. */
    INTERSTITIAL_SHOWN("interstitial_shown"),

    /** A store link in the app-install interstitial was followed. */
    INTERSTITIAL_TAPPED("interstitial_tapped"),

    /** A question was submitted to Vegasful Intelligence. */
    INTELLIGENCE_QUERIED("intelligence_queried"),

    /** Intelligence finished, successfully or not. */
    INTELLIGENCE_RESPONDED("intelligence_responded"),

    /** A call to our own API failed. Tells a funnel drop caused by a bug from one caused by us. */
    API_ERROR("api_error"),

    /** An uncaught script error or rejected promise reached the window. */
    PAGE_ERROR("page_error"),

    /**
     * A horizontal event scroller was advanced. Proposed beyond `analytics-plan.md` §2.3's list,
     * which names no event for the scrollers even though they are most of what the home page is.
     */
    SCROLLER_ADVANCED("scroller_advanced");

    companion object {
        /**
         * The events this surface actually emits. The rest of the enum exists to keep the file
         * diffable against the app's copy; listing the live set here means the report of what web
         * sends is derivable from the code rather than from a document that will drift from it.
         */
        val webEmits: Set<AnalyticsEvent> = setOf(
            PAGE_VIEWED,
            EVENT_VIEWED,
            ENTITY_VIEWED,
            ENTITY_DEPTH_REACHED,
            EVENT_SAVED,
            EVENT_UNSAVED,
            ENTITY_FOLLOWED,
            ENTITY_UNFOLLOWED,
            SEARCH_PERFORMED,
            SEARCH_RESULT_TAPPED,
            TICKET_LINK_TAPPED,
            SHARE_COMPLETED,
            FILTER_APPLIED,
            DATE_SELECTED,
            MAP_INTERACTED,
            SCROLLER_ADVANCED,
            INTERSTITIAL_SHOWN,
            INTERSTITIAL_TAPPED,
            INTELLIGENCE_QUERIED,
            INTELLIGENCE_RESPONDED,
            SIGNED_IN,
            SIGNED_OUT,
            ACCOUNT_DELETED,
            API_ERROR,
            PAGE_ERROR
        )
    }
}

/**
 * Event parameter names, kept alongside the events so the whole taxonomy reads from one file and
 * a typo in a parameter is a one-place fix.
 *
 * Values may be [String], [Int], [Long], [Double] or [Boolean]. gtag will accept other types and
 * then quietly do something unhelpful with them, so the facade drops anything else.
 *
 * Like [AnalyticsEvent], the first block mirrors the app's `AnalyticsParam`.
 */
object AnalyticsParam {
    const val SCREEN_NAME = "screen_name"
    const val SCREEN_CLASS = "screen_class"
    const val EVENT_ID = "event_id"
    const val VENUE_ID = "venue_id"
    const val ENTITY_TYPE = "entity_type"
    const val ENTITY_ID = "entity_id"
    const val SOURCE = "source"
    const val QUERY_LENGTH = "query_length"
    const val RESULTS_COUNT = "results_count"
    const val SEARCH_TYPE = "search_type"
    const val CONTEXT = "context"
    const val CATEGORY = "category"
    const val CAMPAIGN_ID = "campaign_id"
    const val MEDIUM = "medium"
    const val POSITION = "position"
    const val ENABLED = "enabled"
    const val METHOD = "method"
    const val PROVIDER = "provider"
    const val SUCCESS = "success"
    const val INTERESTS_SELECTED = "interests_selected"
    const val SIGNED_IN = "signed_in"
    const val PATH = "path"
    const val GESTURE = "gesture"
    const val STEP = "step"
    const val OUTCOME = "outcome"
    const val SCREEN = "screen"
    const val SELECTED_COUNT = "selected_count"

    // -------------------------------------------------------------------------------------
    // Web additions, matching the web block of [AnalyticsEvent].
    // -------------------------------------------------------------------------------------

    /** The kind of page an event happened on: `home`, `event`, `venue`, `search`, and so on. */
    const val PAGE_TYPE = "page_type"

    /** Which filter group changed, for `filter_applied`. */
    const val FILTER_TYPE = "filter_type"

    /** How many days a chosen date range covers. Zero means the range was cleared. */
    const val RANGE_DAYS = "range_days"

    /** Which way a scroller was advanced: `left` or `right`. */
    const val DIRECTION = "direction"

    /** The store an interstitial link led to: `ios` or `android`. */
    const val PLATFORM = "platform"

    /** An HTTP status, for `api_error`. */
    const val STATUS = "status"

    /** A short, truncated description of a failure. Never a stack trace, never user text. */
    const val MESSAGE = "message"
}

/**
 * The user properties from `docs/retention.md`, mirroring the app's `UserProperty`.
 *
 * The web can only speak to `account_type`; the rest are app concepts. Set when they change, not
 * on every event.
 */
enum class UserProperty(val propertyName: String) {
    ACQUISITION_SOURCE("acquisition_source"),
    FOLLOWS_COUNT("follows_count"),
    PUSH_ENABLED("push_enabled"),
    ACCOUNT_TYPE("account_type"),
    ONBOARDING_INTERESTS("onboarding_interests")
}

/**
 * How the visitor reached the page an event is reported from — the values of `event_viewed.source`
 * in `docs/retention.md`.
 *
 * The first group is shared with the app. The web adds values for the surfaces it has and the app
 * does not, and derives which one applies from the referring page rather than from a navigation
 * stack, since a web page has no memory of what led to it beyond `document.referrer`.
 */
object AnalyticsSource {
    const val HOME = "home"
    const val EXPLORE = "explore"
    const val SEARCH = "search"
    const val PUSH = "push"
    const val DEEP_LINK = "deep_link"
    const val DIGEST = "digest"

    /** Nothing on this site led here — a bookmark, a typed address, or an external referrer. */
    const val DIRECT = "direct"

    // Web additions.

    /** Another page of the site, of a kind with no more specific value above. */
    const val WEB = "web"

    /** The visitor scrolled well into the page. */
    const val SCROLL = "scroll"

    /** A link to a related performer, venue or tag was followed. */
    const val RELATED_LINK = "related_link"

    /** A link the page marked as leading to further detail was followed. */
    const val DETAIL_LINK = "detail_link"
}

/**
 * The gesture behind an `event_saved`, mirroring the app's `SaveGesture`.
 *
 * `event_saved` has no matching `InteractionType`: the enum is LIKE / FOLLOW / SHARE / CALENDAR /
 * TICKETS / MORE_INFO / VIEWED, with no SAVE. Two gestures are candidates, so **both** are
 * reported and each `event_saved` carries which one it was. That lets the activation gate in
 * `docs/retention.md` be computed either way while the decision of which gesture "save" means is
 * still pending there — only the dashboard's filter changes when it lands.
 */
enum class SaveGesture(val analyticsValue: String) {
    /** Adding the event to the calendar — the strongest intent signal in the existing model. */
    CALENDAR("calendar"),

    /** Tapping the heart on an event. */
    LIKE("like")
}

/**
 * The entity type of a Vegasful path — `events/abc` is an `event`. Paths are the only entity
 * handle several of the call sites have. Mirrors the app's `entityTypeOf`.
 */
fun entityTypeOf(path: String): String =
    when (path.removePrefix("/").substringBefore("/")) {
        "events" -> "event"
        "venues" -> "venue"
        "tags" -> "tag"
        "performers" -> "performer"
        "things-to-do" -> "thing"
        else -> "unknown"
    }

/**
 * The entity id of a Vegasful path — `events/abc` is `abc`. Any query string is not part of it.
 * Mirrors the app's `entityIdOf`.
 */
fun entityIdOf(path: String): String =
    path.removePrefix("/").substringBefore("?").substringAfter("/", "")

/**
 * The kind of page a site path names, which is the web's answer to the app's screen name.
 *
 * Entity detail pages report the entity type, so that `page_type` and `entity_type` agree on the
 * pages where both are present. Everything else gets a name for the surface rather than for the
 * URL, which keeps this low-cardinality enough to group on.
 */
fun pageTypeOf(path: String): String {
    val trimmed = path.substringBefore("?").substringBefore("#").removePrefix("/")
    if (trimmed.isEmpty()) return "home"
    val first = trimmed.substringBefore("/")
    val rest = trimmed.substringAfter("/", "")
    return when (first) {
        "events", "venues", "tags", "performers", "things-to-do" ->
            // A bare `/events` is the listing; `/events/abc` is one event.
            if (rest.isEmpty()) "${entityTypeOf(first)}_list" else entityTypeOf(first)

        "search" -> "search"
        "login", "auth" -> "login"
        "account" -> "account"
        "articles" -> "article"
        "map" -> "map"
        "intelligence" -> "intelligence"
        else -> first
    }
}
